import React from "react";
import { motion, useReducedMotion } from "framer-motion";

export function Reveal({ children, delay = 0, y = 28, className = "", ...rest }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function SectionHeading({ overline, title, kicker, align = "left", testid }) {
  return (
    <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"} data-testid={testid}>
      {overline && (
        <Reveal>
          <div className={`overline text-gold mb-5 flex items-center gap-3 ${align === "center" ? "justify-center" : ""}`}>
            <span className="h-px w-8 bg-gold/50" />
            {overline}
          </div>
        </Reveal>
      )}
      <Reveal delay={0.05}>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          {title}
        </h2>
      </Reveal>
      {kicker && (
        <Reveal delay={0.12}>
          <p className={`mt-6 text-silver/70 text-base md:text-lg font-body font-light max-w-2xl ${align === "center" ? "mx-auto" : ""}`}>
            {kicker}
          </p>
        </Reveal>
      )}
    </div>
  );
}

export function Section({ id, className = "", children }) {
  return (
    <section id={id} className={`relative px-6 md:px-10 lg:px-16 py-24 md:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto">{children}</div>
    </section>
  );
}

// Animated node network used for risk / crisis sections
export function NodeNetwork({ className = "" }) {
  const reduced = useReducedMotion();
  const nodes = [
    [20, 30], [50, 15], [80, 28], [30, 60], [65, 55], [88, 72], [15, 80], [50, 88],
  ];
  const links = [[0, 1], [1, 2], [0, 3], [3, 4], [1, 4], [4, 5], [3, 6], [4, 7], [2, 5], [6, 7]];
  return (
    <svg viewBox="0 0 100 100" className={className} preserveAspectRatio="none" aria-hidden="true">
      {links.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]}
          stroke="rgba(212,175,55,0.35)" strokeWidth="0.25"
          initial={reduced ? false : { pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, delay: i * 0.08, ease: "easeInOut" }}
        />
      ))}
      {nodes.map(([x, y], i) => (
        <motion.circle
          key={i} cx={x} cy={y} r="1" fill="#D4AF37"
          initial={reduced ? false : { scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
        >
          {!reduced && (
            <animate attributeName="r" values="1;1.6;1" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          )}
        </motion.circle>
      ))}
    </svg>
  );
}
